import React, { useState, useEffect } from 'react';
import {
  Chip,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Book as BookIcon,
  Person as PersonIcon,
  TouchApp as TouchAppIcon,
  Category as CategoryIcon,
  Storage as StorageIcon,
  Analytics as AnalyticsIcon,
  ReportProblem as ReportIcon,
} from '@mui/icons-material';
import { getMyCourses } from '../services/adminCourseService';
import { CourseDTO } from '../services/adminCourseService';
import { unknownLocationService } from '../services/unknownLocationService';

const SystemAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [myCourses, setMyCourses] = useState<CourseDTO[]>([]);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const courses = await getMyCourses();
      setMyCourses(courses);
      try {
        const countData = await unknownLocationService.getPendingCount();
        setPendingReportsCount(countData.count ?? 0);
      } catch {
        // badge is non-critical — ignore failures
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        System Admin Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonIcon />}
                    onClick={() => navigate('/admin/users')}
                  >
                    Manage Users
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PeopleIcon />}
                    onClick={() => navigate('/admin/system-admins')}
                  >
                    Manage Admins
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SchoolIcon />}
                    onClick={() => navigate('/admin/courses')}
                  >
                    Manage Courses
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<BookIcon />}
                    onClick={() => navigate('/admin/topics')}
                  >
                    Manage Topics
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LocationIcon />}
                    onClick={() => navigate('/admin/locations')}
                  >
                    Manage Locations
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CategoryIcon />}
                    onClick={() => navigate('/admin/course-types')}
                  >
                    Course types
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<TouchAppIcon />}
                    onClick={() => navigate('/admin/course-types/guest-eligibility')}
                  >
                    Try as Guest eligibility
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AnalyticsIcon />}
                    onClick={() => navigate('/admin/guest-analytics')}
                  >
                    Try as Guest Analytics
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<StorageIcon />}
                    onClick={() => navigate('/admin/mcp')}
                  >
                    MCP Server
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant={pendingReportsCount > 0 ? 'contained' : 'outlined'}
                    color={pendingReportsCount > 0 ? 'warning' : 'primary'}
                    startIcon={<ReportIcon />}
                    onClick={() => navigate('/admin/schools')}
                    endIcon={
                      pendingReportsCount > 0
                        ? <Chip label={pendingReportsCount} color="error" size="small" />
                        : undefined
                    }
                  >
                    School Reports
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* My Assigned Courses */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">My Assigned Courses</Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/admin/courses')}
                >
                  View All Courses
                </Button>
              </Box>
              {myCourses.length === 0 ? (
                <Typography color="text.secondary">
                  No courses assigned yet.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {myCourses.slice(0, 6).map((course) => (
                    <Grid item xs={12} sm={6} md={4} key={course.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {course.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            {course.courseType}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Status: {course.status}
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            onClick={() =>
                              navigate(`/admin/courses/${course.id}/topics`)
                            }
                          >
                            Manage Topics
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SystemAdminDashboard;

